import { useRef, useState } from 'react'
import {
  Grid3X3, ZoomIn, ZoomOut, Save, Plus, Eye, Pencil,
  MapPin, ChevronLeft, ChevronRight, Clock,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import { format } from 'date-fns'

interface FloorPlanToolbarProps {
  onSave: () => void
  onAddTable: () => void
  onAddArea: () => void
  saving: boolean
  dirty: boolean
  bookingDates: string[]
  bookingHours: string[]
  // Date layout editing
  editingTimeStart: string
  editingTimeEnd: string
  onNowClick: () => void
  onTimeStartChange: (v: string) => void
  onTimeEndChange: (v: string) => void
  onLayoutDateSelect: (date: string | 'default') => void
  onCancelDateEdit: () => void
  resolvedLayoutName: string | null
  onEditLiveLayout: () => void
  onEditDefaultLayout: () => void
}

interface ScrollRowProps {
  label: string
  empty: string
  isEmpty: boolean
  children: React.ReactNode
}

function ScrollRow({ label, empty, isEmpty, children }: ScrollRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -120 : 120, behavior: 'smooth' })
  }

  return (
    <div className="flex items-center gap-1 min-w-0">
      <span className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider mr-1 shrink-0">
        {label}
      </span>
      <button
        onClick={() => scroll('left')}
        className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors cursor-pointer"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <div
        ref={scrollRef}
        className="flex items-center gap-1 overflow-x-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {isEmpty ? (
          <span className="text-[10px] text-[var(--color-text-muted)] italic px-1 whitespace-nowrap">
            {empty}
          </span>
        ) : (
          children
        )}
      </div>
      <button
        onClick={() => scroll('right')}
        className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors cursor-pointer"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + (m ?? 0)
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

interface TimeScrubberProps {
  bookingHours: string[]
  timelineHour: string | null
  timelineDate: string
  setTimelineHour: (t: string | null) => void
}

function TimeScrubber({ bookingHours, timelineHour, timelineDate, setTimelineHour }: TimeScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const today = format(new Date(), 'yyyy-MM-dd')

  const now = new Date()
  const nowRatio = (now.getHours() * 60 + now.getMinutes()) / 1439
  const handleRatio = timelineHour !== null
    ? Math.max(0, Math.min(1, timeToMinutes(timelineHour) / 1439))
    : null

  // Labels at 00:00, 06:00, 12:00, 18:00, 24:00
  const hourLabels = [0, 360, 720, 1080, 1440]

  function updateFromPointer(e: React.PointerEvent) {
    const rect = trackRef.current!.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const snapped = Math.min(1439, Math.round((ratio * 1439) / 15) * 15)
    setTimelineHour(minutesToTime(snapped))
  }

  function handlePointerDown(e: React.PointerEvent) {
    e.currentTarget.setPointerCapture(e.pointerId)
    dragging.current = true
    setIsDragging(true)
    updateFromPointer(e)
  }
  function handlePointerMove(e: React.PointerEvent) {
    if (!dragging.current) return
    updateFromPointer(e)
  }
  function handlePointerUp(e: React.PointerEvent) {
    e.currentTarget.releasePointerCapture(e.pointerId)
    dragging.current = false
    setIsDragging(false)
  }

  return (
    <div className="w-full">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2 px-0.5">
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">Time</span>
          {timelineDate === today && (
            <button
              onClick={() => setTimelineHour(format(new Date(), 'HH:mm'))}
              className="flex items-center gap-1.5 text-[10px] text-[var(--color-text-muted)] hover:text-red-400 transition-colors cursor-pointer"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block animate-pulse" />
              Now
            </button>
          )}
        </div>
        <span className="text-sm font-mono font-semibold tabular-nums text-[var(--color-text-primary)]">
          {timelineHour ?? '--:--'}
        </span>
      </div>

      {/* Track hit area */}
      <div
        ref={trackRef}
        className="relative h-8 w-full cursor-ew-resize select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {/* Track line */}
        <div className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 bg-[var(--color-border-subtle)]" />

        {/* Booking markers — small amber ticks on the track */}
        {bookingHours.map((t) => {
          const r = timeToMinutes(t) / 1439
          return (
            <div
              key={t}
              style={{ left: `${r * 100}%` }}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-0.5 h-3 rounded-full bg-[var(--color-accent-amber)]/70 pointer-events-none"
            />
          )
        })}

        {/* Now marker */}
        {timelineDate === today && (
          <div
            style={{ left: `${nowRatio * 100}%` }}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-none"
          >
            <div className="w-px h-4 rounded-full bg-red-400/80" />
          </div>
        )}

        {/* Playhead */}
        {handleRatio !== null && (
          <div
            style={{ left: `${handleRatio * 100}%` }}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-none z-10"
          >
            {/* Tooltip — visible while dragging */}
            {isDragging && (
              <div className="absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 whitespace-nowrap">
                <div className="px-2 py-1 rounded-md bg-[var(--color-bg-surface)] border border-[var(--color-accent-amber)]/40 text-xs font-mono font-semibold text-[var(--color-accent-amber)] shadow-lg">
                  {timelineHour}
                </div>
                <div className="w-0 h-0 mx-auto mt-px" style={{ borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid color-mix(in srgb, var(--color-accent-amber) 40%, transparent)' }} />
              </div>
            )}
            {/* Vertical bar */}
            <div className="w-0.5 h-7 rounded-full bg-[var(--color-accent-amber)] mx-auto" />
            {/* Center dot */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--color-accent-amber)] shadow-[0_0_8px_rgba(251,191,36,0.5)]" />
          </div>
        )}
      </div>

      {/* Hour labels */}
      <div className="relative h-4">
        {hourLabels.map((m) => {
          const r = m / 1440
          const label = m === 1440 ? '24' : String(m / 60).padStart(2, '0')
          const align = m === 0 ? 'left-0' : m === 1440 ? 'right-0' : undefined
          return (
            <span
              key={m}
              style={align ? undefined : { left: `${r * 100}%`, transform: 'translateX(-50%)' }}
              className={`absolute top-0 text-[9px] font-mono text-[var(--color-text-muted)]/40 tabular-nums ${align ?? ''}`}
            >
              {label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

export function FloorPlanToolbar({
  onSave,
  onAddTable,
  onAddArea,
  saving,
  dirty,
  bookingDates,
  bookingHours,
  editingTimeStart,
  editingTimeEnd,
  onNowClick,
  onTimeStartChange,
  onTimeEndChange,
  onLayoutDateSelect,
  onCancelDateEdit,
  resolvedLayoutName,
  onEditLiveLayout,
  onEditDefaultLayout,
}: FloorPlanToolbarProps) {
  const {
    snapToGrid, toggleSnap, zoom, setZoom,
    mode, setMode,
    timelineDate, setTimelineDate,
    timelineHour, setTimelineHour,
    isEditingDateLayout,
  } = useFloorPlanStore()

  const today = format(new Date(), 'yyyy-MM-dd')

  const saveLabel = isEditingDateLayout
    ? `Save for ${format(new Date(timelineDate + 'T12:00:00'), 'MMM d')}`
    : 'Save Default'

  return (
    <div className="flex flex-col bg-[var(--color-bg-surface)] border-b border-[var(--color-border-subtle)]">
      {/* Main toolbar row */}
      <div className="flex items-center gap-2 p-3 flex-wrap">
        {/* Mode pill: Live | Default */}
        <div className="flex items-center gap-1 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-1">
          <button
            onClick={() => setMode('live')}
            className={`px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium transition-colors cursor-pointer ${
              mode === 'live'
                ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            }`}
          >
            <Eye className="h-3.5 w-3.5 inline mr-1" /> Live
          </button>
          <button
            onClick={onEditDefaultLayout}
            className={`px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium transition-colors cursor-pointer ${
              mode === 'layout' && !isEditingDateLayout
                ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            }`}
          >
            <Pencil className="h-3.5 w-3.5 inline mr-1" /> Default
          </button>
        </div>

        <div className="w-px h-6 bg-[var(--color-border-subtle)]" />

        <Tooltip content={snapToGrid ? 'Disable snap' : 'Enable snap'}>
          <Button variant={snapToGrid ? 'secondary' : 'ghost'} size="icon-sm" onClick={toggleSnap}>
            <Grid3X3 className="h-4 w-4" />
          </Button>
        </Tooltip>

        <Tooltip content="Zoom out">
          <Button variant="ghost" size="icon-sm" onClick={() => setZoom(zoom - 0.1)}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </Tooltip>
        <span className="text-xs text-[var(--color-text-muted)] w-10 text-center">
          {Math.round(zoom * 100)}%
        </span>
        <Tooltip content="Zoom in">
          <Button variant="ghost" size="icon-sm" onClick={() => setZoom(zoom + 0.1)}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </Tooltip>

        <div className="w-px h-6 bg-[var(--color-border-subtle)]" />

        {mode === 'layout' ? (
          <>
            {isEditingDateLayout ? (
              /* Date-specific editing controls */
              <>
                <Button variant="ghost" size="sm" onClick={onCancelDateEdit}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={onSave} loading={saving} disabled={!dirty}>
                  <Save className="h-3.5 w-3.5" /> {saveLabel}
                </Button>
              </>
            ) : (
              /* Default layout editing controls */
              <>
                <Button variant="secondary" size="sm" onClick={onAddArea}>
                  <MapPin className="h-3.5 w-3.5" /> Add Area
                </Button>
                <Button variant="secondary" size="sm" onClick={onAddTable}>
                  <Plus className="h-3.5 w-3.5" /> Add Table
                </Button>
                <Button variant="primary" size="sm" onClick={onSave} loading={saving} disabled={!dirty}>
                  <Save className="h-3.5 w-3.5" /> Save Default
                </Button>
              </>
            )}
          </>
        ) : (
          /* Live mode: resolved layout badge + Edit button */
          <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
            <span>
              Layout:{' '}
              <span className="text-[var(--color-text-secondary)] font-medium">
                {resolvedLayoutName ?? 'Default'}
              </span>
            </span>
            <Button variant="ghost" size="sm" onClick={onEditLiveLayout}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          </div>
        )}
      </div>

      {/* Date row — hidden in Default layout mode */}
      {!(mode === 'layout' && !isEditingDateLayout) && (
        <div className="flex items-center gap-4 px-3 pb-2">
          {/* Date scroller */}
          <ScrollRow label="Day" empty="" isEmpty={false}>
            {mode === 'layout' && (
              <button
                onClick={() => onLayoutDateSelect('default')}
                className={`px-2 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  !isEditingDateLayout
                    ? 'bg-[var(--color-accent-amber)] text-[#1a1714]'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                }`}
              >
                Default
              </button>
            )}

            {mode === 'live' && (
              <button
                onClick={() => setTimelineDate(today)}
                className={`px-2 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  timelineDate === today
                    ? 'bg-[var(--color-accent-amber)] text-[#1a1714]'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                }`}
              >
                Today
              </button>
            )}

            <input
              type="date"
              min={today}
              value={
                mode === 'layout'
                  ? (isEditingDateLayout && !bookingDates.includes(timelineDate) ? timelineDate : '')
                  : (!bookingDates.includes(timelineDate) && timelineDate !== today ? timelineDate : '')
              }
              onChange={(e) => {
                if (!e.target.value) return
                mode === 'layout' ? onLayoutDateSelect(e.target.value) : setTimelineDate(e.target.value)
              }}
              title="Pick a date"
              className="text-[10px] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border-subtle)] rounded-full px-2 py-1 cursor-pointer hover:text-[var(--color-text-secondary)] transition-colors"
              style={{ colorScheme: 'dark' }}
            />

            {(mode === 'live' ? bookingDates.filter((d) => d !== today) : bookingDates).map((d) => {
              const isActive = mode === 'live' ? d === timelineDate : (isEditingDateLayout && d === timelineDate)
              const dayLabel = format(new Date(d + 'T12:00:00'), 'EEE d MMM')
              return (
                <button
                  key={d}
                  onClick={() => mode === 'layout' ? onLayoutDateSelect(d) : setTimelineDate(d)}
                  className={`px-2 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-[var(--color-accent-amber)] text-[#1a1714]'
                      : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                  }`}
                >
                  {dayLabel}
                </button>
              )
            })}
          </ScrollRow>

          {/* Time section — only shown in non-live modes inline */}
          {mode !== 'live' && (
            <>
              <div className="w-px h-5 bg-[var(--color-border-subtle)] shrink-0" />
              {isEditingDateLayout ? (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider shrink-0">Time</span>
                  {timelineDate === today && (
                    <button
                      onClick={onNowClick}
                      className="px-2 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                    >
                      Now
                    </button>
                  )}
                  <div className="flex items-center gap-1">
                    <input
                      type="time"
                      value={editingTimeStart}
                      onChange={(e) => onTimeStartChange(e.target.value)}
                      className="text-[10px] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] border border-[var(--color-border-subtle)] rounded-[var(--radius-sm)] px-1.5 py-1 cursor-pointer"
                    />
                    <span className="text-[10px] text-[var(--color-text-muted)]">→</span>
                    <input
                      type="time"
                      value={editingTimeEnd}
                      onChange={(e) => onTimeEndChange(e.target.value)}
                      className="text-[10px] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] border border-[var(--color-border-subtle)] rounded-[var(--radius-sm)] px-1.5 py-1 cursor-pointer"
                    />
                  </div>
                </div>
              ) : (
                <ScrollRow label="Time" empty="No bookings on this day" isEmpty={false}>
                  {bookingHours.length === 0 ? (
                    <span className="text-[10px] text-[var(--color-text-muted)] italic px-1">No bookings on this day</span>
                  ) : (
                    bookingHours.map((t) => (
                      <span key={t} className="px-2 py-1 rounded-full text-[10px] font-medium bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] whitespace-nowrap">
                        {t}
                      </span>
                    ))
                  )}
                </ScrollRow>
              )}
            </>
          )}
        </div>
      )}

      {/* Time scrubber — live mode only, full-width dedicated row */}
      {mode === 'live' && (
        <div className="px-3 pb-3">
          <TimeScrubber
            bookingHours={bookingHours}
            timelineHour={timelineHour}
            timelineDate={timelineDate}
            setTimelineHour={setTimelineHour}
          />
        </div>
      )}
    </div>
  )
}
